// frontend\src\pages\Calculators\components\Exposure\Exposure.js
import { useState } from "react";
import { toast } from "react-toastify";
import ShutterSpeedInput from "./ShutterSpeedInput";
import { COMMON_APERTURES } from "./constants";

const Exposure = ({ onCalculate }) => {
  const [aperture, setAperture] = useState("2.8");
  const [shutterSpeed, setShutterSpeed] = useState("1/125");
  const [iso, setIso] = useState("100");
  const [exposureComp, setExposureComp] = useState("0");
  const [useReciprocity, setUseReciprocity] = useState(false);

  const STANDARD_SHUTTER_SPEEDS = [
    1 / 8000, 1 / 4000, 1 / 2000, 1 / 1000, 1 / 500, 1 / 250, 1 / 125, 
    1 / 60, 1 / 30, 1 / 15, 1 / 8, 1 / 4, 1 / 2, 1, 2, 4, 8, 15, 30, 
    60, 120, 240, 480, 960, 1920,
  ];

  const parseShutterSpeed = (speed) => {
    if (speed.includes("/")) {
      const parts = speed.split("/");
      return parts.length === 2 ? 1 / parseFloat(parts[1]) : 0;
    }
    return parseFloat(speed);
  };

  const formatShutterSpeed = (seconds) => {
    if (seconds >= 1) {
      return `${Math.round(seconds)} сек`;
    }
    const fraction = Math.round(1 / seconds);
    return fraction > 1 ? `1/${fraction} сек` : `${seconds.toFixed(3)} сек`;
  };

  const findClosestShutterSpeed = (value) => {
    return STANDARD_SHUTTER_SPEEDS.reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
  };

  function getReciprocityCorrection(shutterSpeed) {
    if (shutterSpeed <= 1 / 8) {
      return 0;
    } else if (shutterSpeed <= 1) {
      return 0.25;
    } else if (shutterSpeed <= 8) {
      return 0.5;
    } else if (shutterSpeed <= 30) {
      return 1;
    } else if (shutterSpeed <= 120) {
      return 1.5;
    } else {
      return 2;
    }
  }

  const applyReciprocityCorrection = (shutterSpeed) => {
    if (!useReciprocity) {
      return shutterSpeed;
    }
    const reciprocityCorrection = getReciprocityCorrection(shutterSpeed);
    return shutterSpeed * 2 ** reciprocityCorrection;
  };

  const calculateEquivalentPairs = (baseAperture, baseShutterSpeed) => {
    return COMMON_APERTURES.map((aperture) => {
      const exactShutterSpeed = (aperture ** 2 / baseAperture ** 2) * baseShutterSpeed;
      const closestSpeed = findClosestShutterSpeed(exactShutterSpeed);

      return {
        aperture,
        shutterSpeed: closestSpeed,
        evDifference: Math.log2(closestSpeed / exactShutterSpeed),
      };
    }).filter((pair) => pair.shutterSpeed <= 1920);
  };

  const handleCalculate = () => {
    const fStop = parseFloat(aperture);
    let speedValue = parseShutterSpeed(shutterSpeed);
    const filmSpeed = parseFloat(iso);
    const comp = parseFloat(exposureComp);

    if (isNaN(fStop) || fStop <= 0) {
      toast.error("Диафрагма должна быть положительным числом");
      return;
    }

    if (speedValue <= 0) {
      toast.error("Введите корректное значение выдержки");
      return;
    }

    if (isNaN(filmSpeed) || filmSpeed <= 0) {
      toast.error("ISO должно быть положительным числом");
      return;
    }

    const exactCorrectedSpeedValue = applyReciprocityCorrection(speedValue);
    const roundedCorrectedSpeedValue = findClosestShutterSpeed(exactCorrectedSpeedValue);

    const equivalentPairs = calculateEquivalentPairs(fStop, exactCorrectedSpeedValue);

    const currentEV = Math.log2(fStop ** 2 / exactCorrectedSpeedValue) + 
                     Math.log2(filmSpeed / 100) + comp;

    onCalculate(
      <div className="calculator-result">
        <div className="mb-4">
          <h5 className="mb-3">Текущая экспозиция</h5>
          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
            <span>Экспозиция:</span>
            <strong className="text-primary">{currentEV.toFixed(1)} EV</strong>
          </div>
          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
            <span>Диафрагма:</span>
            <strong>ƒ/{fStop.toFixed(1)}</strong>
          </div>
          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
            <span>ISO:</span>
            <strong>{filmSpeed}</strong>
          </div>
          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
            <span>Выдержка:</span>
            <strong>
              {formatShutterSpeed(useReciprocity ? roundedCorrectedSpeedValue : speedValue)}
            </strong>
          </div>
          {comp !== 0 && (
            <div className="d-flex justify-content-between align-items-center py-2">
              <span>Экспокоррекция:</span>
              <strong className={comp > 0 ? "text-success" : "text-danger"}>
                {comp > 0 ? "+" : ""}{comp} EV
              </strong>
            </div>
          )}
        </div>

        <div>
          <h5 className="mb-3">Эквивалентные экспопары</h5>
          <div className="row g-2">
            {equivalentPairs.map((pair, i) => (
              <div key={i} className="col-6 col-sm-4 col-md-3 col-lg-2">
                <div className="card text-center h-100">
                  <div className="card-body p-2">
                    <div className="fw-bold text-primary">ƒ/{pair.aperture}</div>
                    <div className="small text-muted">
                      {formatShutterSpeed(pair.shutterSpeed)}
                    </div>
                    {Math.abs(pair.evDifference) > 0.05 && (
                      <div className={`small ${pair.evDifference > 0 ? "text-success" : "text-danger"}`}>
                        {pair.evDifference > 0 ? "+" : ""}
                        {pair.evDifference.toFixed(1)} EV
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container">
      <h2 className="mb-4">Калькулятор экспозиции</h2>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">Диафрагма (ƒ/)</label>
          <select
            className="form-select"
            value={aperture}
            onChange={(e) => setAperture(e.target.value)}
          >
            {COMMON_APERTURES.map((a, i) => (
              <option key={i} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">Выдержка</label>
          <ShutterSpeedInput
            value={shutterSpeed}
            onChange={setShutterSpeed}
            allowLongExposures
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">Чувствительность ISO</label>
          <input
            type="number"
            className="form-control"
            value={iso}
            onChange={(e) => setIso(e.target.value)}
            min="25"
            step="1"
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">Экспокоррекция (EV)</label>
          <input
            type="number"
            className="form-control"
            value={exposureComp}
            onChange={(e) => setExposureComp(e.target.value)}
            min="-5"
            max="5"
            step="0.5"
          />
        </div>
      </div>

      <div className="mb-4">
        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="reciprocity"
            checked={useReciprocity}
            onChange={() => setUseReciprocity(!useReciprocity)}
          />
          <label className="form-check-label" htmlFor="reciprocity">
            Коррекция эффекта взаимности (Reciprocity failure)
          </label>
        </div>
        <div className="alert alert-info">
          <h6 className="alert-heading mb-2">
            Компенсация потери чувствительности плёнки на длинных выдержках:
          </h6>
          <ul className="mb-0 small">
            <li>от 1/4 до 1 секунды - коррекция <strong>0.25 EV</strong></li>
            <li>от 2 до 8 секунд - коррекция <strong>0.5 EV</strong></li>
            <li>от 15 до 30 секунд - коррекция <strong>1 EV</strong></li>
            <li>от 60 до 120 секунд - коррекция <strong>1.5 EV</strong></li>
            <li>более 120 секунд - коррекция <strong>2 EV</strong></li>
          </ul>
        </div>
      </div>

      <button className="btn btn-primary w-100" onClick={handleCalculate}>
        Рассчитать экспозицию
      </button>
    </div>
  );
};

export default Exposure;